<template>
  <div class="trend-card trend-card--summary">
    <div class="trend-card__body">
      <div class="trend-loading summary-loading" v-if="isLoading">
        Loading...
      </div>

      <div class="trend-empty summary-empty" v-else-if="hasError">
        {{ errorMsg || "Failed to load" }}
      </div>

      <div class="uw-summary-split" v-else>
        <div class="uw-summary-card uw-summary-card--apps">
          <div class="uw-summary-card__top">
            <div class="uw-summary-card__title">
              <i class="fa-duotone fa-layer-group"></i>
              Applications
            </div>
          </div>

          <div class="uw-summary-card__grid uw-summary-card__grid--two">
            <button class="uw-mini-metric uw-mini-metric--click" type="button" @click.stop="openApps()">
              <div class="uw-mini-metric__label">Incomplete Apps</div>
              <div class="uw-mini-metric__value">{{ allApps90Count }}</div>
            </button>

            <button class="uw-mini-metric uw-mini-metric--click" type="button" @click.stop="openAppsSubmittedLast5()">
              <div class="uw-mini-metric__label">Submitted (5 days)</div>
              <div class="uw-mini-metric__value">{{ submittedLast5Count }}</div>
            </button>
          </div>

          <div class="uw-summary-card__footer">
            <span class="uw-summary-card__footer-left">
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
            </span>
            <span class="uw-summary-card__footer-right">
              Updated {{ lastUpdatedLabel }}
            </span>
          </div>
        </div>

        <div class="uw-summary-card uw-summary-card--tasks">
          <div class="uw-summary-card__top">
            <div class="uw-summary-card__title">
              <i class="fa-duotone fa-list-check"></i>
              Tasks
            </div>
          </div>

          <div class="uw-summary-card__grid uw-summary-card__grid--two">
            <button class="uw-mini-metric uw-mini-metric--click" type="button" @click.stop="openLOSignature()">
              <div class="uw-mini-metric__label">Pending LO Signature</div>
              <div class="uw-mini-metric__value">{{ pendingLOSignatureCount }}</div>
            </button>

            <button
              class="uw-mini-metric uw-mini-metric--click"
              type="button"
              @click.stop="openOutstandingInternalTasks()"
            >
              <div class="uw-mini-metric__label">Internal tasks</div>
              <div class="uw-mini-metric__value">{{ pendingAssignedInternalTasksCount }}</div>
            </button>
          </div>

          <div class="uw-summary-card__footer">
            <span class="uw-summary-card__footer-left">
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
            </span>
            <span class="uw-summary-card__footer-right">
              Updated {{ lastUpdatedLabel }}
            </span>
          </div>
        </div>

        <div class="uw-summary-card uw-summary-card--pipe">
          <div class="uw-summary-card__top">
            <div class="uw-summary-card__title">
              <i class="fa-duotone fa-diagram-project"></i>
              Pipeline
            </div>
          </div>

          <div class="uw-summary-card__grid uw-summary-card__grid--four">
            <button class="uw-mini-metric uw-mini-metric--click" type="button" @click.stop="openExpiringLocks()">
              <div class="uw-mini-metric__label">Expiring locks</div>
              <div class="uw-mini-metric__value">{{ expiringLocksCount }}</div>
            </button>

            <button
              class="uw-mini-metric uw-mini-metric--click"
              type="button"
              @click.stop="openPendingBorrowerSignatures()"
            >
              <div class="uw-mini-metric__label">Pending borrower signatures</div>
              <div class="uw-mini-metric__value">{{ pendingBorrowerSignaturesCount }}</div>
            </button>

            <button
              class="uw-mini-metric uw-mini-metric--click"
              type="button"
              @click.stop="openPendingBorrowerTasks()"
            >
              <div class="uw-mini-metric__label">Pending borrower tasks</div>
              <div class="uw-mini-metric__value">{{ pendingBorrowerTasksCount }}</div>
            </button>

            <button
              class="uw-mini-metric uw-mini-metric--click"
              type="button"
              @click.stop="openRequestedBorrowerTasks()"
            >
              <div class="uw-mini-metric__label">Requested borrower tasks</div>
              <div class="uw-mini-metric__value">{{ requestedBorrowerTasksCount }}</div>
            </button>
          </div>

          <div class="uw-summary-card__footer">
            <span class="uw-summary-card__footer-left">
              <i class="fa-duotone fa-arrow-up-right-from-square"></i>
            </span>
            <span class="uw-summary-card__footer-right">
              Updated {{ lastUpdatedLabel }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="aiModal.open" class="uw-ai-modal-backdrop" @click.self="closeSwiftAiModal">
      <div class="uw-ai-modal">
        <div class="uw-ai-modal__header">
          <div class="uw-ai-modal__title-wrap">
            <div class="uw-ai-modal__eyebrow">SwiftAI</div>
            <div class="uw-ai-modal__title">Pipeline Intelligence</div>
            <div class="uw-ai-modal__subtitle">
              <template v-if="aiModal.step === 'prompt'">Choose recent submitted apps to review</template>
              <template v-else-if="aiModal.loading">Reviewing your applications</template>
              <template v-else>Analysis complete</template>
            </div>
          </div>

          <button class="uw-ai-modal__close" type="button" @click="closeSwiftAiModal">
            <i class="fa-duotone fa-xmark"></i>
          </button>
        </div>

        <div v-if="aiModal.step === 'prompt'" class="uw-ai-prompt">
          <div class="uw-ai-prompt__icon">
            <i class="fa-duotone fa-sparkles"></i>
          </div>

          <div class="uw-ai-prompt__title">Build pipeline analysis</div>
          <div class="uw-ai-prompt__text">
            Choose how many recent days of submitted applications SwiftAI should review.
          </div>

          <div class="uw-ai-prompt__form">
            <label class="uw-ai-prompt__label" for="swiftAiDaysBack">Days back</label>
            <input
              id="swiftAiDaysBack"
              v-model.number="aiDaysBack"
              class="uw-ai-prompt__input"
              type="number"
              min="1"
              max="90"
            />

            <div class="uw-ai-prompt__hint">
              {{ aiScopedApps.length }} submitted application<span v-if="aiScopedApps.length !== 1">s</span> will be included
            </div>
          </div>

          <div class="uw-ai-prompt__quick">
            <button type="button" class="uw-ai-quick-btn" @click="setAiDays(5)">5 days</button>
            <button type="button" class="uw-ai-quick-btn" @click="setAiDays(10)">10 days</button>
            <button type="button" class="uw-ai-quick-btn" @click="setAiDays(15)">15 days</button>
            <button type="button" class="uw-ai-quick-btn" @click="setAiDays(30)">30 days</button>
          </div>
        </div>

        <div v-else-if="aiModal.loading" class="uw-ai-loading">
          <div class="uw-ai-loading__bar">
            <div class="uw-ai-loading__bar-fill" :style="{ width: aiProgressPercent + '%' }"></div>
          </div>

          <div class="uw-ai-loading__percent">{{ aiProgressPercent }}%</div>
          <div class="uw-ai-loading__message">{{ aiLoadingMessage }}</div>

          <div class="uw-ai-loading__steps">
            <div
              v-for="(msg, idx) in aiLoadingMessages"
              :key="idx"
              class="uw-ai-loading__step"
              :class="{ 'is-active': idx <= aiLoadingMessageIndex }"
            >
              <i class="fa-duotone" :class="idx <= aiLoadingMessageIndex ? 'fa-circle-check' : 'fa-circle-notch'"></i>
              <span>{{ msg }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="aiModal.error" class="uw-ai-error">
          <div class="uw-ai-error__icon">
            <i class="fa-duotone fa-triangle-exclamation"></i>
          </div>
          <div class="uw-ai-error__title">Could not build pipeline analysis</div>
          <div class="uw-ai-error__text">{{ aiModal.error }}</div>
        </div>

        <div v-else-if="aiResult" class="uw-ai-content">
          <div class="uw-ai-tabs">
            <button
              type="button"
              class="uw-ai-tab"
              :class="{ 'is-active': aiTab === 'overview' }"
              @click="aiTab = 'overview'"
            >
              Overview
            </button>
            <button
              type="button"
              class="uw-ai-tab"
              :class="{ 'is-active': aiTab === 'rankings' }"
              @click="aiTab = 'rankings'"
            >
              Rankings
            </button>
            <button
              type="button"
              class="uw-ai-tab"
              :class="{ 'is-active': aiTab === 'borrowers' }"
              @click="aiTab = 'borrowers'"
            >
              Borrowers
            </button>
            <button
              type="button"
              class="uw-ai-tab"
              :class="{ 'is-active': aiTab === 'risks' }"
              @click="aiTab = 'risks'"
            >
              Risks
            </button>
          </div>

          <div class="uw-ai-panel" v-if="aiTab === 'overview'">
            <div class="uw-ai-stat-grid">
              <div class="uw-ai-stat">
                <div class="uw-ai-stat__label">Applications</div>
                <div class="uw-ai-stat__value">{{ aiResult.summary?.totalApplications || 0 }}</div>
              </div>

              <div class="uw-ai-stat">
                <div class="uw-ai-stat__label">Estimated Volume</div>
                <div class="uw-ai-stat__value">{{ money(aiResult.summary?.estimatedTotalVolume) }}</div>
              </div>

              <div class="uw-ai-stat">
                <div class="uw-ai-stat__label">Highest Priority</div>
                <div class="uw-ai-stat__value">{{ topRankedBorrower }}</div>
              </div>
            </div>

            <div class="uw-ai-section">
              <div class="uw-ai-section__title">Pipeline mix</div>

              <div class="uw-ai-pipeline-chart">
                <div
                  v-for="item in pipelinePriorityRows"
                  :key="item.key"
                  class="uw-ai-pipeline-row"
                >
                  <div class="uw-ai-pipeline-row__label">
                    <span class="uw-priority-pill" :class="priorityClass(item.key)">
                      {{ item.label }}
                    </span>
                  </div>

                  <div class="uw-ai-pipeline-row__bar-wrap">
                    <div class="uw-ai-pipeline-row__bar">
                      <div
                        class="uw-ai-pipeline-row__fill"
                        :class="priorityFillClass(item.key)"
                        :style="{ width: pipelineBarWidth(item.count) }"
                      ></div>
                    </div>
                  </div>

                  <div class="uw-ai-pipeline-row__count">{{ item.count }}</div>
                </div>
              </div>
            </div>

            <div class="uw-ai-section">
              <div class="uw-ai-section__title">Top opportunities</div>
              <div class="uw-ai-opportunity-list">
                <div
                  v-for="(item, idx) in aiResult.summary?.topOpportunities || []"
                  :key="idx"
                  class="uw-ai-opportunity"
                >
                  <div class="uw-ai-opportunity__top">
                    <div class="uw-ai-opportunity__name">{{ item.borrowerName }}</div>
                    <div class="uw-ai-opportunity__amount">{{ money(item.loanAmount) }}</div>
                  </div>
                  <div class="uw-ai-opportunity__reason">{{ item.reason }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="uw-ai-panel" v-if="aiTab === 'rankings'">
            <div class="uw-ai-table-wrap">
              <table class="uw-ai-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Borrower</th>
                    <th>LO</th>
                    <th>Purpose</th>
                    <th>State</th>
                    <th>Loan Amt</th>
                    <th>Approval</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in aiResult.dealRankings || []" :key="row.applicationId">
                    <td>{{ row.rank }}</td>
                    <td>
                      <div class="uw-ai-cell-title">{{ row.borrowerName }}</div>
                      <div class="uw-ai-cell-sub">{{ row.applicationId }}</div>
                    </td>
                    <td>{{ row.loanOfficerName }}</td>
                    <td>{{ row.loanPurpose || "—" }}</td>
                    <td>{{ row.propertyState || "—" }}</td>
                    <td>{{ money(row.estimatedLoanAmount) }}</td>
                    <td>
                      <div class="uw-ai-score">
                        <div class="uw-ai-score__bar">
                          <div class="uw-ai-score__fill" :style="{ width: row.approvalProbability + '%' }"></div>
                        </div>
                        <span>{{ row.approvalProbability }}%</span>
                      </div>
                    </td>
                    <td>
                      <span class="uw-priority-pill" :class="priorityClass(row.priority)">
                        {{ row.priority }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="uw-ai-panel" v-if="aiTab === 'borrowers'">
            <div class="uw-ai-borrower-list">
              <div
                v-for="card in aiResult.borrowerCards || []"
                :key="card.displayName + card.loanPurpose"
                class="uw-ai-borrower-card"
              >
                <div class="uw-ai-borrower-card__top">
                  <div>
                    <div class="uw-ai-borrower-card__name">{{ card.displayName }}</div>
                    <div class="uw-ai-borrower-card__meta">
                      {{ card.loanOfficerName }} · {{ card.loanPurpose }} · {{ card.propertyCity }}, {{ card.propertyState }}
                    </div>
                  </div>

                  <div class="uw-ai-borrower-card__score">
                    <div class="uw-ai-borrower-card__score-num">{{ card.approvalProbability }}%</div>
                    <span class="uw-priority-pill" :class="priorityClass(card.priority)">
                      {{ card.priority }}
                    </span>
                  </div>
                </div>

                <div class="uw-ai-borrower-card__summary">{{ card.summary }}</div>

                <div class="uw-ai-borrower-card__row">
                  <div class="uw-ai-chip">
                    <i class="fa-duotone fa-money-bill"></i>
                    {{ money(card.estimatedLoanAmount) }}
                  </div>
                  <div class="uw-ai-chip">
                    <i class="fa-duotone fa-house"></i>
                    {{ card.occupancy || "—" }}
                  </div>
                </div>

                <div class="uw-ai-subsection">
                  <div class="uw-ai-subsection__title">Risk flags</div>
                  <div class="uw-ai-chip-list">
                    <span v-for="(risk, idx) in card.riskFlags || []" :key="idx" class="uw-ai-chip uw-ai-chip--risk">
                      {{ risk }}
                    </span>
                  </div>
                </div>

                <div class="uw-ai-subsection">
                  <div class="uw-ai-subsection__title">Action items</div>
                  <ul class="uw-ai-list">
                    <li v-for="(item, idx) in card.actionItems || []" :key="idx">{{ item }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="uw-ai-panel" v-if="aiTab === 'risks'">
            <div class="uw-ai-section">
              <div class="uw-ai-section__title">Key risks</div>
              <ul class="uw-ai-list uw-ai-list--large">
                <li v-for="(risk, idx) in aiResult.summary?.keyRisks || []" :key="idx">
                  {{ risk }}
                </li>
              </ul>
            </div>

            <div class="uw-ai-section">
              <div class="uw-ai-section__title">Scoring model</div>
              <div class="uw-ai-bands">
                <div class="uw-ai-band">
                  <div class="uw-ai-band__label">Strong</div>
                  <div class="uw-ai-band__text">{{ aiResult.scoringModel?.bands?.strong || "—" }}</div>
                </div>
                <div class="uw-ai-band">
                  <div class="uw-ai-band__label">Workable</div>
                  <div class="uw-ai-band__text">{{ aiResult.scoringModel?.bands?.workable || "—" }}</div>
                </div>
                <div class="uw-ai-band">
                  <div class="uw-ai-band__label">Caution</div>
                  <div class="uw-ai-band__text">{{ aiResult.scoringModel?.bands?.caution || "—" }}</div>
                </div>
                <div class="uw-ai-band">
                  <div class="uw-ai-band__label">Weak</div>
                  <div class="uw-ai-band__text">{{ aiResult.scoringModel?.bands?.weak || "—" }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="uw-ai-modal__footer">
          <button class="uw-ai-secondary-btn" type="button" @click="closeSwiftAiModal">
            {{ aiModal.step === 'prompt' ? 'Cancel' : 'Close' }}
          </button>

          <button
            v-if="aiModal.step === 'prompt'"
            class="uw-ai-primary-btn"
            type="button"
            :disabled="!aiScopedApps.length || aiModal.loading"
            @click="openSwiftAiPipeline()"
          >
            Analyze {{ aiScopedApps.length }} app<span v-if="aiScopedApps.length !== 1">s</span>
          </button>

          <button
            v-else-if="!aiModal.loading"
            class="uw-ai-primary-btn"
            type="button"
            @click="openSwiftAiPrompt(true)"
          >
            Refresh analysis
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import auth from "@/authConfig.js"
import apiService from "@/api/api.service.js"

export default {
  name: "LoanOfficerTopSummary",
  props: {
    azureId: { type: String, default: "" },
    mineOnly: { type: Boolean, default: true },
    applicationsDaysBack: { type: Number, default: 90 },
    applicationsRoute: { type: String, default: "/Applications" },
    openTaskColumns: { type: Array, default: () => [] },
    internalTaskColumns: { type: Array, default: () => [] },
    expiringLocksColumns: { type: Array, default: () => [] },
    userEncompassId: { type: String, default: "" }
  },
  emits: ["open-grid", "open-disclosures"],
  data() {
    return {
      nowTick: Date.now(),
      _nowTimer: null,
      pipelineQuery: null,
      borrowerPendingTaskData: [],
      borrowerRequestedTaskData: [],
      apps: [],
      expiringLocksUserLoans: [],
      expiringLocksBranchLoans: [],
      openSigningTaskData: [],
      internalTaskRequested: [],
      pendingLOSignatureData: [],

      isLoading: false,
      hasError: false,
      errorMsg: "",
      lastUpdatedAt: null,

      _reqId: 0,

      aiModal: {
        open: false,
        loading: false,
        error: "",
        step: "prompt"
      },
      aiDaysBack: 5,
      aiTab: "overview",
      aiResult: null,
      aiProgressPercent: 0,
      aiLoadingMessageIndex: 0,
      aiLoadingTimer: null,
      aiProgressTimer: null,
      aiLoadingMessages: [
        "Collecting recent submitted applications",
        "Parsing borrower scenarios",
        "Grouping duplicate borrower records",
        "Reviewing income and liability signals",
        "Scoring approval probability",
        "Ranking pipeline opportunities",
        "Building pipeline intelligence"
      ]
    }
  },
  computed: {
    pendingBorrowerTasksCount() {
      return Array.isArray(this.borrowerPendingTaskData) ? this.borrowerPendingTaskData.length : 0
    },
    requestedBorrowerTasksCount() {
      return Array.isArray(this.borrowerRequestedTaskData) ? this.borrowerRequestedTaskData.length : 0
    },
    safeApps() {
      return Array.isArray(this.apps) ? this.apps : []
    },
    scopedApps() {
      if (!this.mineOnly) return this.safeApps
      if (!this.azureId) return []
      const me = String(this.azureId).toLowerCase()
      return this.safeApps.filter(a => String(a?.azureId || "").toLowerCase() === me)
    },
    aiScopedApps() {
      const days = Math.max(1, Number(this.aiDaysBack || 5))
      const cut = this._daysAgo(days)
      return this.scopedApps.filter(a => this._submittedOnOrAfter(a, cut))
    },
    expiringLocksActive() {
      return this.mineOnly ? this.expiringLocksUserLoans : this.expiringLocksBranchLoans
    },
    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return "—"

      const updated = new Date(this.lastUpdatedAt).getTime()
      if (Number.isNaN(updated)) return "—"

      const diffSec = Math.max(0, Math.floor((this.nowTick - updated) / 1000))

      if (diffSec < 5) return "just now"
      if (diffSec < 60) return `${diffSec}s ago`

      const diffMin = Math.floor(diffSec / 60)
      if (diffMin < 60) return `${diffMin}m ago`

      const diffHr = Math.floor(diffMin / 60)
      return `${diffHr}h ago`
    },
    allApps90Count() {
      return this.scopedApps.filter(a => !this._parseSwiftDate(a?.submitted)).length
    },
    submittedLast5Count() {
      const cut = this._daysAgo(5)
      return this.scopedApps.filter(a => this._submittedOnOrAfter(a, cut)).length
    },
    expiringLocksCount() {
      return Array.isArray(this.expiringLocksActive) ? this.expiringLocksActive.length : 0
    },
    pendingBorrowerSignaturesCount() {
      if (!Array.isArray(this.openSigningTaskData)) return 0

      return this.openSigningTaskData.filter(x =>
        !x.title?.toLowerCase().includes("accept econsent")
      ).length
    },
    pendingAssignedInternalTasksCount() {
      return Array.isArray(this.internalTaskRequested) ? this.internalTaskRequested.length : 0
    },
    pendingLOSignatureCount() {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const matchingPackages = (this.pendingLOSignatureData || [])
        .flatMap(disclosure =>
          (disclosure.packages || []).filter(packageItem =>
            (packageItem.recipients || []).some(recipient =>
              recipient.sentDate &&
              new Date(recipient.sentDate) >= thirtyDaysAgo &&
              recipient.id?.toLowerCase() === this.userEncompassId?.toLowerCase() &&
              (recipient.taskStatuses || []).some(s =>
                s.taskType?.toLowerCase() === 'esign' &&
                s.status?.toLowerCase() === 'incomplete'
              )
            )
          )
        );

      const pendingLOSigTotal = matchingPackages.length;
      return pendingLOSigTotal;
    },
    aiLoadingMessage() {
      return this.aiLoadingMessages[this.aiLoadingMessageIndex] || "Working..."
    },
    topRankedBorrower() {
      return this.aiResult?.dealRankings?.[0]?.borrowerName || "—"
    },
    pipelinePriorityCounts() {
      const counts = {
        highest: 0,
        high: 0,
        medium: 0,
        low: 0,
        ignore: 0
      }

      const rows = this.aiResult?.dealRankings || []

      for (const row of rows) {
        const key = String(row?.priority || "").toLowerCase()
        if (Object.prototype.hasOwnProperty.call(counts, key)) {
          counts[key] += 1
        }
      }

      return counts
    },
    pipelinePriorityRows() {
      return [
        { key: "highest", label: "Highest", count: this.pipelinePriorityCounts.highest },
        { key: "high", label: "High", count: this.pipelinePriorityCounts.high },
        { key: "medium", label: "Medium", count: this.pipelinePriorityCounts.medium },
        { key: "low", label: "Low", count: this.pipelinePriorityCounts.low },
        { key: "ignore", label: "Ignore", count: this.pipelinePriorityCounts.ignore }
      ]
    },
    pipelineChartMax() {
      const vals = this.pipelinePriorityRows.map(x => x.count)
      const max = Math.max(...vals, 1)
      return max < 1 ? 1 : max
    }
  },
  methods: {
    _startUpdatedTimer() {
      if (this._nowTimer) return
      this._nowTimer = setInterval(() => {
        this.nowTick = Date.now()
      }, 1000)
    },

    _stopUpdatedTimer() {
      if (!this._nowTimer) return
      clearInterval(this._nowTimer)
      this._nowTimer = null
    },

    async refresh() {
      if (!this.azureId) return

      const reqId = ++this._reqId

      this.isLoading = true
      this.hasError = false
      this.errorMsg = ""

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const accessToken = tokenResponse.accessToken

        const qRes = await apiService.GetSwiftAppPipelineQueryResponse(this.azureId, accessToken)
        if (reqId !== this._reqId) return
        this.pipelineQuery = qRes?.data?.data || null

        const tasks = [
          this._fetchApps(accessToken, reqId),
          this._fetchExpiringLocks(accessToken, reqId),
          this._fetchBorrowerSigningTasks(accessToken, reqId),
          this._fetchInternalTasks(accessToken, reqId),
          this._fetchLOSignature(accessToken, reqId)
        ]

        await Promise.allSettled(tasks)

        if (reqId !== this._reqId) return
        this.lastUpdatedAt = new Date().toISOString()
      } catch (error) {
        if (reqId !== this._reqId) return
        this.hasError = true
        this.errorMsg = error?.response?.data?.data || error?.message || "Failed to load"
      } finally {
        if (reqId === this._reqId) this.isLoading = false
      }
    },

    async _fetchApps(accessToken, reqId) {
      const res = await apiService.GetApplicationsByAzureIdAndStartedDate(
        this.pipelineQuery,
        this.azureId,
        this.applicationsDaysBack,
        accessToken
      )
      if (reqId !== this._reqId) return
      this.apps = res?.data?.data || []
    },

    async _fetchExpiringLocks(accessToken, reqId) {
      const res = await apiService.GetExpiringLocks(this.azureId, accessToken)
      const data = res?.data?.data ?? {}
      if (reqId !== this._reqId) return

      this.expiringLocksBranchLoans = (data.branchloans ?? []).map(t => ({
        ...t,
        cust17fv: this._normalizeDate(t.cust17fv)
      }))

      this.expiringLocksUserLoans = (data.userloans ?? []).map(t => ({
        ...t,
        cust17fv: this._normalizeDate(t.cust17fv)
      }))
    },

   async _fetchBorrowerSigningTasks(accessToken, reqId) {
  const res = await apiService.GetSwiftDashboardData(
    this.azureId,
    !this.mineOnly,
    !this.mineOnly,
    accessToken
  )

  const raw = res?.data?.data?.data ?? res?.data?.data ?? []
  if (reqId !== this._reqId) return

  const signingList = []
  const borrowerPendingList = []
  const borrowerRequestedList = []

  for (let i = raw.length - 1; i >= 0; i--) {
    const item = raw[i]
    const status = String(item?.status || "").trim().toLowerCase()
    const signing = String(item?.signingtask || "").trim().toLowerCase()
    const completed = !!item?.completed

    const next = {
      ...item,
      created: this._normalizeDate(item.created),
      cust17fv: this._normalizeDate(item.cust17fv)
    }

    if (status === "requested" && signing === "true") {
      signingList.unshift(next)
    }

    if (!completed && status === "pending") {
      borrowerPendingList.unshift(next)
    }

    if (!completed && status === "requested") {
      borrowerRequestedList.unshift(next)
    }
  }

  this.openSigningTaskData = signingList
  this.borrowerPendingTaskData = borrowerPendingList
  this.borrowerRequestedTaskData = borrowerRequestedList
},

    async _fetchInternalTasks(accessToken, reqId) {
      const res = this.mineOnly
        ? await apiService.GetInternalTasks(this.azureId, accessToken)
        : await apiService.GetInternalTasksByOrgId(this.azureId, accessToken)

      const raw = res?.data?.data?.data ?? res?.data?.data ?? []
      if (reqId !== this._reqId) return

      const requested = []

      for (let i = raw.length - 1; i >= 0; i--) {
        const item = raw[i]
        const status = String(item?.status || "")
        const completed = !!item?.completed

        const next = {
          ...item,
          followupdate: this._normalizeDate(item.followupdate),
          created: this._normalizeDate(item.created)
        }

        if (completed) continue

        if (status === "Pending") {
          next.status = "Open"
          requested.unshift(next)
        } else if (status === "Requested") {
          next.status = "Assigned"
          requested.unshift(next)
        } else if (status === "In Process") {
          requested.unshift(next)
        } else if (!status) {
          requested.unshift(next)
        }
      }

      this.internalTaskRequested = requested
    },

    async _fetchLOSignature(accessToken, reqId) {
      const res = await apiService.GetLODisclosuresCount(this.azureId, accessToken)
      if (reqId !== this._reqId) return
      this.pendingLOSignatureData = res?.data?.data ?? []
    },

    buildApplicationsUrl(tabName) {
      const host = window.location.hostname
      const isLocal = host === "localhost"
      const isDev = host === "dev.swiftapp.ai"
      const isProd = host === "swiftapp.ai" || host === "www.swiftapp.ai"

      const baseUrl = isLocal
        ? "http://localhost:8080"
        : isDev
          ? "https://dev.swiftapp.ai"
          : isProd
            ? "https://swiftapp.ai"
            : "https://swiftapp.ai"

      const qs = new URLSearchParams()
      qs.set("tab", tabName)

      if (this.mineOnly && this.azureId) {
        qs.set("azureId", this.azureId)
      }

      return `${baseUrl}${this.applicationsRoute}?${qs.toString()}`
    },

    openApps() {
      window.open(this.buildApplicationsUrl("incomplete"), "_blank")
    },

    openAppsSubmittedLast5() {
      window.open(this.buildApplicationsUrl("submitted"), "_blank")
    },

    openPendingBorrowerSignatures() {
      const filteredTasks = Array.isArray(this.openSigningTaskData)
        ? this.openSigningTaskData.filter(x =>
            !x.title?.toLowerCase().includes("accept econsent")
          )
        : []

      this.$emit("open-grid", {
        title: "Pending Borrower Signature",
        data: this.groupByLoanBorrower(filteredTasks),
        columns: this.openTaskColumns,
        sendNotiButton: true,
        dialogType: "borrowerTaskGrouped"
      })
    },

    openPendingBorrowerTasks() {
      this.$emit("open-grid", {
        title: "Pending Tasks",
        data: this.groupByLoanBorrower(this.borrowerPendingTaskData),
        columns: this.openTaskColumns,
        sendNotiButton: true,
        dialogType: "borrowerTaskGrouped"
      })
    },

    openRequestedBorrowerTasks() {
      this.$emit("open-grid", {
        title: "Requested Borrower Tasks",
        data: this.groupByLoanBorrower(this.borrowerRequestedTaskData),
        columns: this.openTaskColumns,
        sendNotiButton: true,
        dialogType: "borrowerTaskGrouped"
      })
    },

    openOutstandingInternalTasks() {
      this.$emit("open-grid", {
        title: "Outstanding Loan Tasks",
        data: this.groupByLoanBorrower(this.internalTaskRequested),
        columns: this.internalTaskColumns,
        dialogType: "internalTaskGrouped"
      })
    },

    openExpiringLocks() {
      this.$emit("open-grid", {
        title: "Expiring Locks",
        data: this.expiringLocksActive,
        columns: this.expiringLocksColumns,
        requestLockExtensionButton: true
      })
    },

    openLOSignature() {
      this.$emit("open-disclosures")
    },

    cleanText(v) {
      if (v == null) return ""
      const s = String(v).trim()
      if (!s) return ""
      const low = s.toLowerCase()
      if (low === "undefined" || low === "null") return ""
      return s
    },

    getLoanNumber(item) {
      return this.cleanText(item?.loannumber || item?.f364 || "")
    },

    getBorrowerName(item) {
      return this.cleanText(item?.borrowername || item?.borrower_name || item?.name || "—") || "—"
    },

    groupByLoanBorrower(items) {
      const map = new Map()

      for (const t of items || []) {
        const loanNumber = this.getLoanNumber(t) || "—"
        const borrower = this.getBorrowerName(t) || "—"
        const key = `${loanNumber}__${borrower}`

        if (!map.has(key)) {
          map.set(key, { loanNumber, borrowers: [borrower], tasks: [] })
        }

        map.get(key).tasks.push(t)
      }

      return Array.from(map.values()).sort((a, b) => {
        const la = String(a.loanNumber || "")
        const lb = String(b.loanNumber || "")
        if (la !== lb) return la.localeCompare(lb)
        const ba = String((a.borrowers || [])[0] || "")
        const bb = String((b.borrowers || [])[0] || "")
        return ba.localeCompare(bb)
      })
    },

    _daysAgo(days) {
      const d = new Date()
      d.setDate(d.getDate() - days)
      d.setHours(0, 0, 0, 0)
      return d
    },

    _submittedOnOrAfter(a, dateObj) {
      const dt = this._parseSwiftDate(a?.submitted)
      if (!dt) return false
      return dt >= dateObj
    },

    _parseSwiftDate(raw) {
      if (!raw) return null
      if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw

      const s = String(raw).trim()
      if (!s) return null

      const isoTry = new Date(s)
      if (!isNaN(isoTry.getTime())) return isoTry

      const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/)
      if (!m) return null

      const mm = parseInt(m[1], 10)
      const dd = parseInt(m[2], 10)
      const yyyy = parseInt(m[3], 10)
      const hh = parseInt(m[4], 10)
      const mi = parseInt(m[5], 10)
      const ss = parseInt(m[6], 10)

      const dt = new Date(yyyy, mm - 1, dd, hh, mi, ss)
      return isNaN(dt.getTime()) ? null : dt
    },

    _normalizeDate(v) {
      if (!v) return null
      const d = new Date(v)
      return isNaN(d.getTime()) ? null : d
    },

    money(v) {
      const n = Number(v || 0)
      if (!Number.isFinite(n)) return "$0"
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0
      }).format(n)
    },

    priorityClass(priority) {
      const p = String(priority || "").toLowerCase()
      if (p === "highest") return "uw-priority-pill--highest"
      if (p === "high") return "uw-priority-pill--high"
      if (p === "medium") return "uw-priority-pill--medium"
      if (p === "low") return "uw-priority-pill--low"
      return "uw-priority-pill--ignore"
    },

    priorityFillClass(priority) {
      const p = String(priority || "").toLowerCase()
      if (p === "highest") return "uw-ai-pipeline-row__fill--highest"
      if (p === "high") return "uw-ai-pipeline-row__fill--high"
      if (p === "medium") return "uw-ai-pipeline-row__fill--medium"
      if (p === "low") return "uw-ai-pipeline-row__fill--low"
      return "uw-ai-pipeline-row__fill--ignore"
    },

    pipelineBarWidth(count) {
      const max = this.pipelineChartMax || 1
      const width = Math.max(6, Math.round((count / max) * 100))
      return `${width}%`
    },

    setAiDays(days) {
      this.aiDaysBack = days
    },

    openSwiftAiPrompt(reset = false) {
      this.aiModal.open = true
      this.aiModal.loading = false
      this.aiModal.error = ""
      this.aiModal.step = "prompt"
      this.aiTab = "overview"

      if (reset) {
        this.aiResult = null
      }

      if (!this.aiDaysBack || this.aiDaysBack < 1) {
        this.aiDaysBack = 5
      }
    },

    _startAiLoadingUi() {
      this._stopAiLoadingUi()

      this.aiProgressPercent = 3
      this.aiLoadingMessageIndex = 0

      let rawProgress = 3
      const maxBeforeComplete = 92

      const tick = () => {
        if (!this.aiModal.loading) return
        if (rawProgress >= maxBeforeComplete) {
          this.aiProgressPercent = maxBeforeComplete
          return
        }

        let step = 0

        if (rawProgress < 18) {
          step = 0.8 + Math.random() * 1.9
        } else if (rawProgress < 45) {
          step = 0.35 + Math.random() * 1.15
        } else if (rawProgress < 70) {
          step = 0.15 + Math.random() * 0.75
        } else if (rawProgress < 84) {
          step = 0.05 + Math.random() * 0.35
        } else {
          step = 0.02 + Math.random() * 0.12
        }

        const shouldPause = Math.random() < 0.14
        if (!shouldPause) {
          rawProgress += step
        }

        if (rawProgress > maxBeforeComplete) {
          rawProgress = maxBeforeComplete
        }

        this.aiProgressPercent = Math.max(
          this.aiProgressPercent,
          Math.min(maxBeforeComplete, Math.round(rawProgress + (Math.random() * 0.6 - 0.2)))
        )

        const nextDelay =
          rawProgress < 20
            ? 900 + Math.floor(Math.random() * 700)
            : rawProgress < 50
              ? 1100 + Math.floor(Math.random() * 900)
              : rawProgress < 75
                ? 1400 + Math.floor(Math.random() * 1200)
                : 1800 + Math.floor(Math.random() * 1800)

        this.aiProgressTimer = setTimeout(tick, nextDelay)
      }

      const advanceMessage = () => {
        if (!this.aiModal.loading) return

        if (this.aiLoadingMessageIndex < this.aiLoadingMessages.length - 1) {
          this.aiLoadingMessageIndex += 1
        }

        const nextDelay = 9000 + Math.floor(Math.random() * 7000)
        this.aiLoadingTimer = setTimeout(advanceMessage, nextDelay)
      }

      this.aiProgressTimer = setTimeout(tick, 1000 + Math.floor(Math.random() * 900))
      this.aiLoadingTimer = setTimeout(advanceMessage, 8000 + Math.floor(Math.random() * 4000))
    },

    _stopAiLoadingUi() {
      if (this.aiLoadingTimer) {
        clearTimeout(this.aiLoadingTimer)
        this.aiLoadingTimer = null
      }

      if (this.aiProgressTimer) {
        clearTimeout(this.aiProgressTimer)
        this.aiProgressTimer = null
      }
    },

    closeSwiftAiModal() {
      this.aiModal.open = false
      this.aiModal.loading = false
      this.aiModal.error = ""
      this.aiModal.step = "prompt"
      this._stopAiLoadingUi()
    },

    async openSwiftAiPipeline() {
      if (!this.aiScopedApps.length) return

      this.aiModal.open = true
      this.aiModal.loading = true
      this.aiModal.error = ""
      this.aiModal.step = "loading"
      this.aiTab = "overview"
      this.aiResult = null

      this._startAiLoadingUi()

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const accessToken = tokenResponse.accessToken

        const res = await apiService.GetLoanPipelineAnalysis(this.aiScopedApps, accessToken)
        const payload = res?.data?.data || null

        this.aiProgressPercent = 100
        this.aiLoadingMessageIndex = this.aiLoadingMessages.length - 1

        await new Promise(resolve => setTimeout(resolve, 900))

        this.aiResult = payload
        this.aiModal.loading = false
        this.aiModal.step = "result"
      } catch (error) {
        this.aiModal.loading = false
        this.aiModal.error =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to generate pipeline analysis"
        this.aiModal.step = "error"
      } finally {
        this._stopAiLoadingUi()
      }
    }
  },

  mounted() {
    this._startUpdatedTimer()
    this.refresh()
  },

  beforeUnmount() {
    this._stopUpdatedTimer()
    this._stopAiLoadingUi()
  },

  watch: {
    azureId: {
      immediate: true,
      handler() {
        this.refresh()
      }
    },
    mineOnly() {
      this.refresh()
    }
  }
}
</script>

<style scoped>
.trend-card {
  background: var(--background);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.trend-card__body {
  padding: 10px 12px;
  min-height: 0;
}

.trend-loading,
.trend-empty {
  height: auto;
  min-height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  gap: 10px;
}

.uw-summary-split {
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  gap: 10px;
}

.uw-summary-card {
  border: 1px solid var(--border-ltr);
  background: rgba(40, 110, 255, 0.04);
  border-radius: 12px;
  padding: 10px 10px;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 120px;
}

.uw-summary-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
}

.uw-summary-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.uw-summary-card__title {
  font-weight: 950;
  font-size: 12px;
  color: var(--text-primary-on-background);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uw-summary-card__grid {
  display: grid;
  align-items: center;
}

.uw-summary-card__grid--two {
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.uw-summary-card__grid--four {
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 10px;
}

.uw-mini-metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.55);
  text-align: left;
}

.uw-mini-metric--click {
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
}

.uw-mini-metric--click:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.06);
}

.uw-mini-metric--click:active {
  transform: translateY(0px) scale(0.99);
}

.uw-mini-metric__label {
  font-size: 11px;
  font-weight: 900;
  opacity: 0.75;
}

.uw-mini-metric__value {
  font-size: 22px;
  font-weight: 950;
  line-height: 1.1;
}

.uw-summary-card__footer {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background);
}

.uw-summary-card__footer-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.uw-summary-card__footer-right {
  opacity: 0.85;
}

.uw-summary-card__title i {
  font-size: 14px;
  line-height: 1;
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.10));
  --fa-secondary-opacity: 1;
}

.uw-summary-card--apps .uw-summary-card__title i {
  --fa-primary-color: #2563eb;
  --fa-secondary-color: #93c5fd;
}

.uw-summary-card--tasks .uw-summary-card__title i {
  --fa-primary-color: #f59e0b;
  --fa-secondary-color: #fde68a;
}

.uw-summary-card--pipe .uw-summary-card__title i {
  --fa-primary-color: #a855f7;
  --fa-secondary-color: #f0abfc;
}

.uw-summary-card__footer-left i {
  font-size: 12px;
  --fa-primary-color: rgba(15, 23, 42, 0.75);
  --fa-secondary-color: rgba(15, 23, 42, 0.35);
  --fa-secondary-opacity: 1;
}

.uw-summary-card--apps { border-color: rgba(37, 99, 235, 0.18); }
.uw-summary-card--tasks { border-color: rgba(245, 158, 11, 0.18); }
.uw-summary-card--pipe { border-color: rgba(168, 85, 247, 0.18); }

.uw-ai-button {
  appearance: none;
  border: 1px solid rgba(37, 99, 235, 0.22);
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(168, 85, 247, 0.12));
  color: var(--text-primary-on-background);
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 11px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
  white-space: nowrap;
}

.uw-ai-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(37, 99, 235, 0.10);
}

.uw-ai-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.uw-ai-button i {
  --fa-primary-color: #7c3aed;
  --fa-secondary-color: #c4b5fd;
  --fa-secondary-opacity: 1;
}

.uw-ai-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.uw-ai-modal {
  width: min(1180px, 96vw);
  max-height: 88vh;
  overflow: hidden;
  border-radius: 20px;
  background: var(--background);
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
}

.uw-ai-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.uw-ai-modal__eyebrow {
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7c3aed;
}

.uw-ai-modal__title {
  font-size: 22px;
  font-weight: 950;
  line-height: 1.1;
  color: var(--text-primary-on-background);
}

.uw-ai-modal__subtitle {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-modal__close {
  appearance: none;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.7);
  width: 38px;
  height: 38px;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.uw-ai-modal__close i {
  font-size: 16px;
}

.uw-ai-prompt,
.uw-ai-loading,
.uw-ai-error {
  padding: 28px 20px;
}

.uw-ai-prompt {
  text-align: center;
}

.uw-ai-prompt__icon {
  font-size: 28px;
}

.uw-ai-prompt__icon i {
  --fa-primary-color: #7c3aed;
  --fa-secondary-color: #c4b5fd;
  --fa-secondary-opacity: 1;
}

.uw-ai-prompt__title {
  margin-top: 10px;
  font-size: 22px;
  font-weight: 950;
  color: var(--text-primary-on-background);
}

.uw-ai-prompt__text {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-prompt__form {
  margin: 18px auto 0;
  max-width: 260px;
  text-align: left;
}

.uw-ai-prompt__label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 900;
}

.uw-ai-prompt__input {
  width: 100%;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.8);
  padding: 0 12px;
  font-size: 14px;
  font-weight: 900;
  outline: none;
}

.uw-ai-prompt__hint {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-prompt__quick {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.uw-ai-quick-btn {
  appearance: none;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.76);
  border-radius: 999px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 900;
}

.uw-ai-loading__bar {
  height: 12px;
  background: rgba(15, 23, 42, 0.08);
  border-radius: 999px;
  overflow: hidden;
}

.uw-ai-loading__bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #7c3aed);
  border-radius: 999px;
  transition: width 280ms ease;
}

.uw-ai-loading__percent {
  margin-top: 12px;
  font-size: 28px;
  font-weight: 950;
  color: var(--text-primary-on-background);
}

.uw-ai-loading__message {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 900;
  color: var(--text-secondary-on-background);
}

.uw-ai-loading__steps {
  margin-top: 18px;
  display: grid;
  gap: 10px;
}

.uw-ai-loading__step {
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0.45;
  font-weight: 800;
  font-size: 13px;
}

.uw-ai-loading__step.is-active {
  opacity: 1;
}

.uw-ai-loading__step i {
  font-size: 14px;
  --fa-primary-color: #2563eb;
  --fa-secondary-color: #cbd5e1;
  --fa-secondary-opacity: 1;
}

.uw-ai-error {
  text-align: center;
}

.uw-ai-error__icon {
  font-size: 28px;
  color: #dc2626;
}

.uw-ai-error__title {
  margin-top: 10px;
  font-size: 18px;
  font-weight: 950;
}

.uw-ai-error__text {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-content {
  padding: 0 20px 20px;
  overflow: auto;
}

.uw-ai-tabs {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--background);
  display: flex;
  gap: 8px;
  padding: 14px 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.uw-ai-tab {
  appearance: none;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.76);
  border-radius: 999px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 900;
}

.uw-ai-tab.is-active {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.14), rgba(124, 58, 237, 0.14));
  border-color: rgba(37, 99, 235, 0.22);
}

.uw-ai-panel {
  padding-top: 16px;
}

.uw-ai-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.uw-ai-stat {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.62);
  padding: 14px;
}

.uw-ai-stat__label {
  font-size: 12px;
  font-weight: 900;
  color: var(--text-secondary-on-background);
}

.uw-ai-stat__value {
  margin-top: 6px;
  font-size: 28px;
  font-weight: 950;
  line-height: 1.1;
}

.uw-ai-section {
  margin-top: 18px;
}

.uw-ai-section__title {
  font-size: 14px;
  font-weight: 950;
  margin-bottom: 10px;
}

.uw-ai-pipeline-chart {
  display: grid;
  gap: 10px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.62);
  padding: 14px;
}

.uw-ai-pipeline-row {
  display: grid;
  grid-template-columns: 88px 1fr 32px;
  gap: 10px;
  align-items: center;
}

.uw-ai-pipeline-row__label {
  display: flex;
  align-items: center;
}

.uw-ai-pipeline-row__bar-wrap {
  min-width: 0;
}

.uw-ai-pipeline-row__bar {
  height: 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.uw-ai-pipeline-row__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 260ms ease;
}

.uw-ai-pipeline-row__fill--highest {
  background: linear-gradient(90deg, #1d4ed8, #60a5fa);
}

.uw-ai-pipeline-row__fill--high {
  background: linear-gradient(90deg, #047857, #34d399);
}

.uw-ai-pipeline-row__fill--medium {
  background: linear-gradient(90deg, #b45309, #fbbf24);
}

.uw-ai-pipeline-row__fill--low {
  background: linear-gradient(90deg, #c2410c, #fb923c);
}

.uw-ai-pipeline-row__fill--ignore {
  background: linear-gradient(90deg, #b91c1c, #f87171);
}

.uw-ai-pipeline-row__count {
  text-align: right;
  font-size: 12px;
  font-weight: 950;
}

.uw-ai-opportunity-list {
  display: grid;
  gap: 10px;
}

.uw-ai-opportunity {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.62);
  padding: 14px;
}

.uw-ai-opportunity__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.uw-ai-opportunity__name {
  font-size: 14px;
  font-weight: 950;
}

.uw-ai-opportunity__amount {
  font-size: 14px;
  font-weight: 950;
  color: #2563eb;
}

.uw-ai-opportunity__reason {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-table-wrap {
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
}

.uw-ai-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
  background: rgba(255, 255, 255, 0.72);
}

.uw-ai-table th,
.uw-ai-table td {
  padding: 12px 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  text-align: left;
  vertical-align: top;
  font-size: 12px;
  font-weight: 800;
}

.uw-ai-table th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary-on-background);
  background: rgba(248, 250, 252, 0.95);
  position: sticky;
  top: 0;
}

.uw-ai-cell-title {
  font-weight: 950;
  font-size: 13px;
}

.uw-ai-cell-sub {
  margin-top: 2px;
  color: var(--text-secondary-on-background);
  font-size: 11px;
}

.uw-ai-score {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 130px;
}

.uw-ai-score__bar {
  position: relative;
  height: 8px;
  flex: 1;
  background: rgba(15, 23, 42, 0.08);
  border-radius: 999px;
  overflow: hidden;
}

.uw-ai-score__fill {
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #7c3aed);
}

.uw-priority-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 68px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 950;
  text-transform: capitalize;
  border: 1px solid transparent;
}

.uw-priority-pill--highest {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  border-color: rgba(37, 99, 235, 0.18);
}

.uw-priority-pill--high {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  border-color: rgba(16, 185, 129, 0.18);
}

.uw-priority-pill--medium {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
  border-color: rgba(245, 158, 11, 0.18);
}

.uw-priority-pill--low {
  background: rgba(249, 115, 22, 0.12);
  color: #c2410c;
  border-color: rgba(249, 115, 22, 0.18);
}

.uw-priority-pill--ignore {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
  border-color: rgba(239, 68, 68, 0.18);
}

.uw-ai-borrower-list {
  display: grid;
  gap: 14px;
}

.uw-ai-borrower-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.7);
  padding: 16px;
}

.uw-ai-borrower-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.uw-ai-borrower-card__name {
  font-size: 16px;
  font-weight: 950;
}

.uw-ai-borrower-card__meta {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-borrower-card__score {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.uw-ai-borrower-card__score-num {
  font-size: 24px;
  font-weight: 950;
}

.uw-ai-borrower-card__summary {
  margin-top: 12px;
  font-size: 13px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-borrower-card__row {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.uw-ai-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.uw-ai-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  font-size: 11px;
  font-weight: 900;
}

.uw-ai-chip--risk {
  background: rgba(239, 68, 68, 0.10);
  color: #b91c1c;
}

.uw-ai-subsection {
  margin-top: 14px;
}

.uw-ai-subsection__title {
  font-size: 12px;
  font-weight: 950;
  margin-bottom: 8px;
}

.uw-ai-list {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  color: var(--text-primary-on-background);
}

.uw-ai-list--large {
  gap: 10px;
}

.uw-ai-bands {
  display: grid;
  gap: 10px;
}

.uw-ai-band {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.65);
  padding: 12px 14px;
}

.uw-ai-band__label {
  font-size: 12px;
  font-weight: 950;
  margin-bottom: 4px;
}

.uw-ai-band__text {
  font-size: 13px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.uw-ai-modal__footer {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.uw-ai-secondary-btn,
.uw-ai-primary-btn {
  appearance: none;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 950;
  cursor: pointer;
}

.uw-ai-secondary-btn {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.75);
}

.uw-ai-primary-btn {
  border: 1px solid rgba(37, 99, 235, 0.18);
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(124, 58, 237, 0.12));
  color: var(--text-primary-on-background);
}

@media (max-width: 1200px) {
  .uw-summary-split {
    grid-template-columns: 1fr;
  }

  .uw-ai-stat-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .uw-ai-modal {
    width: 100%;
    max-height: 95vh;
    border-radius: 16px;
  }

  .uw-ai-modal-backdrop {
    padding: 10px;
  }

  .uw-ai-borrower-card__top {
    flex-direction: column;
    align-items: flex-start;
  }

  .uw-ai-borrower-card__score {
    align-items: flex-start;
  }

  .uw-ai-tabs {
    overflow: auto;
    white-space: nowrap;
  }

  .uw-ai-pipeline-row {
    grid-template-columns: 78px 1fr 26px;
  }

  .uw-summary-card__grid--three,
  .uw-summary-card__grid--four {
    grid-template-columns: 1fr;
  }
}

.uw-summary-card--apps {
  background: rgba(40, 110, 255, 0.04);
}

.uw-summary-card--tasks {
  background: rgba(40, 110, 255, 0.025);
}

.uw-summary-card--pipe {
  background: rgba(200, 80, 255, 0.06);
}

.uw-summary-card__grid--three {
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

.trend-card--summary {
  overflow-y: auto;
}
</style>